import React from 'react';
import { FileText, CheckCircle, AlertTriangle, Scale, Handshake, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const TerminosServicioPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-white pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="mb-12">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-zinc-500 hover:text-[#14532D] transition-colors mb-6"
          >
            <ArrowLeft size={18} />
            Volver al inicio
          </Link>
          
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-[#90EE90]/20 p-4 rounded-2xl">
              <FileText className="text-[#14532D] w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-zinc-900">Términos de Servicio</h1>
              <p className="text-zinc-500 mt-1">Última actualización: Marzo 2026</p>
            </div>
          </div>
          
          <p className="text-lg text-zinc-600 leading-relaxed">
            Bienvenido a Zenex Paraguay. Al utilizar nuestros servicios, aceptás los siguientes términos y condiciones. 
            Te recomendamos leerlos detenidamente.
          </p>
        </div>

        {/* Content */}
        <div className="space-y-10">
          {/* Section 1 */}
          <section className="bg-white rounded-2xl border border-zinc-200 p-8">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="text-[#90EE90] w-6 h-6" />
              <h2 className="text-xl font-bold text-zinc-900">1. Aceptación de los Términos</h2>
            </div>
            <div className="text-zinc-600 space-y-4">
              <p>
                Al acceder y utilizar el sitio web de Zenex Paraguay y/o contratar nuestros servicios, 
                aceptás estar sujeto a estos términos de servicio, todas las leyes y regulaciones aplicables.
              </p>
              <p>
                Si no estás de acuerdo con alguno de estos términos, te pedimos que no utilices nuestro sitio 
                ni contrates nuestros servicios.
              </p>
            </div>
          </section>

          {/* Section 2 */}
          <section className="bg-white rounded-2xl border border-zinc-200 p-8">
            <div className="flex items-center gap-3 mb-4">
              <Handshake className="text-[#90EE90] w-6 h-6" />
              <h2 className="text-xl font-bold text-zinc-900">2. Servicios Ofrecidos</h2>
            </div>
            <div className="text-zinc-600 space-y-4">
              <p>Zenex Paraguay ofrece los siguientes servicios:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Instalación de sistemas de energía solar fotovoltaica</li>
                <li>Venta de equipos solares (paneles, inversores, baterías)</li>
                <li>Gestoría para conexión a red eléctrica (Ley 7599/25)</li>
                <li>Mantenimiento y monitoreo de sistemas solares</li>
                <li>Asesoría técnica y financiera</li>
              </ul>
              <p className="mt-4">
                Los presupuestos generados por nuestra calculadora solar son estimaciones y están sujetos 
                a una visita técnica para confirmar las condiciones específicas de cada instalación.
              </p>
            </div>
          </section>

          {/* Section 3 */}
          <section className="bg-white rounded-2xl border border-zinc-200 p-8">
            <div className="flex items-center gap-3 mb-4">
              <Scale className="text-[#90EE90] w-6 h-6" />
              <h2 className="text-xl font-bold text-zinc-900">3. Garantías</h2>
            </div>
            <div className="text-zinc-600 space-y-4">
              <p>Nuestros productos y servicios incluyen las siguientes garantías:</p>
              <div className="bg-zinc-50 rounded-xl p-6 mt-4">
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-[#90EE90] w-5 h-5 mt-0.5 flex-shrink-0" />
                    <span><strong>Paneles solares:</strong> 25 años de garantía de rendimiento</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-[#90EE90] w-5 h-5 mt-0.5 flex-shrink-0" />
                    <span><strong>Inversores:</strong> 10 años de garantía del fabricante</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-[#90EE90] w-5 h-5 mt-0.5 flex-shrink-0" />
                    <span><strong>Baterías:</strong> Según especificaciones del fabricante</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-[#90EE90] w-5 h-5 mt-0.5 flex-shrink-0" />
                    <span><strong>Instalación:</strong> 2 años de garantía de mano de obra</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section className="bg-white rounded-2xl border border-zinc-200 p-8">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="text-[#90EE90] w-6 h-6" />
              <h2 className="text-xl font-bold text-zinc-900">4. Limitación de Responsabilidad</h2>
            </div>
            <div className="text-zinc-600 space-y-4">
              <p>Zenex Paraguay no será responsable por:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Daños causados por mal uso de los equipos instalados</li>
                <li>Modificaciones no autorizadas al sistema</li>
                <li>Fenómenos naturales excepcionales no cubiertos por la garantía</li>
                <li>Interrupciones del servicio eléctrico de la ANDE</li>
                <li>Cambios en la normativa o tarifas eléctricas</li>
              </ul>
              <p className="mt-4">
                Los ahorros proyectados son estimaciones basadas en el consumo declarado y condiciones 
                promedio de radiación solar. Los resultados reales pueden variar.
              </p>
            </div>
          </section>

          {/* Section 5 */}
          <section className="bg-white rounded-2xl border border-zinc-200 p-8">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="text-[#90EE90] w-6 h-6" />
              <h2 className="text-xl font-bold text-zinc-900">5. Propiedad Intelectual</h2>
            </div>
            <div className="text-zinc-600 space-y-4">
              <p>
                Todo el contenido de este sitio web, incluyendo textos, gráficos, logos, imágenes, 
                y software, es propiedad de Zenex Paraguay o de sus proveedores de contenido y está 
                protegido por las leyes de propiedad intelectual de Paraguay y tratados internacionales.
              </p>
              <p>
                No está permitida la reproducción, distribución o modificación del contenido sin 
                autorización previa por escrito.
              </p>
            </div>
          </section>

          {/* Section 6 */}
          <section className="bg-white rounded-2xl border border-zinc-200 p-8">
            <div className="flex items-center gap-3 mb-4">
              <Scale className="text-[#90EE90] w-6 h-6" />
              <h2 className="text-xl font-bold text-zinc-900">6. Ley Aplicable y Jurisdicción</h2>
            </div>
            <div className="text-zinc-600 space-y-4">
              <p>
                Estos términos se rigen por las leyes de la República del Paraguay. 
                Cualquier disputa será sometida a la jurisdicción de los tribunales ordinarios 
                de la ciudad de Asunción.
              </p>
            </div>
          </section>

          {/* Section 7 - Contact */}
          <section className="bg-gradient-to-br from-[#14532D] to-[#1a5c34] rounded-2xl p-8 text-white">
            <h2 className="text-xl font-bold mb-4">¿Tenés preguntas?</h2>
            <p className="text-white/80 mb-6">
              Si tenés dudas sobre estos términos de servicio, no dudes en contactarnos:
            </p>
            <div className="space-y-2">
              <p><strong>Email:</strong> zenexparaguay@gmail.com</p>
              <p><strong>WhatsApp:</strong> +595 991 344956</p>
              <p><strong>Dirección:</strong> Facundo Machain 7390, casi Francis Morices, Asunción, Paraguay</p>
            </div>
          </section>
        </div>

        {/* Footer note */}
        <div className="mt-12 text-center text-zinc-500 text-sm">
          <p>
            Estos términos de servicio pueden ser actualizados periódicamente. 
            Te notificaremos sobre cambios significativos.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TerminosServicioPage;
